<template>
  <div v-on:keydown.stop>
    <v-data-table
      v-if="rows"
      :items="rows"
      :items-per-page="-1"
      :group-by="groupBy"
      ref="table"
      class="hide-first-column position-relative user-story-map"
      style="padding-top: 40px;"
    >
      <template v-slot:bottom></template>
      <template v-slot:headers></template>

      <template v-slot:no-data>
        <!-- replaced by <card-quick-add> -->
      </template>

      <template #group-header="props">
        {{ props.isGroupOpen(props.item) ? '' : props.toggleGroup(props.item) }}
        <div style="background: #868E99; left: 24px; height: 1px; width: calc(100% + 40px); position: absolute;"></div>
        <!-- the following line is the bottom border, we check for index 0 in order to only render it once -->
        <div v-if="props.index === 0"
             style="background: #868E99; left: 24px; top: calc(100% - 1px); height: 1px; width: calc(100% + 40px); position: absolute;"
        ></div>
        <div
          :ref="`release-${props.item.value}`"
          class="usm-release text-truncate"
          :class="{
            'first': releases.length === 0 || (releases[0]?.value === props.item.value),
            'last': props.item.value === 'unassigned'
          }"
          :style="{
            width: releaseHeights[props.item.value] + 'px',
            transform: 'translateY(-17px) rotate(270deg) translate(-50%, -' + (releaseHeights[props.item.value]/2 - 12) + 'px)'
          }"
        >
          <quick-rename-input
            :data-release="props.item.value"
            @dragover="dragoverGroup($event, props.item.value)"
            :value="getReleaseName(props.item.value)"
            :title="$store.state.isWorkflowDisabled ? '' : 'Click to edit release name'"
            :disabled="$store.state.isWorkflowDisabled || props.item.value === 'unassigned' || props.item.value === 'z-release-placeholder'"
            @onChange="saveReleaseName(props.item.value, $event)"
            class="release-name-display"
            density="2"
            :maxLength="30"
          />
        </div>
      </template>
      <template #item="props">
        <tr :key="props.item.release + '-' + props.item.rowInRelease">
          <td class="pre-column"></td>
          <td class="row-start-margin"></td>
          <template v-for="(column, index) in columns" :key="index">
            <td
              :data-column="index"
              :data-row="props.item.rowInRelease"
              :data-release="props.item.release"
              class="text-left description-column pt-1"
              @click="focusOnThisCell"
              @dragover="dragoverCell"
              @dragenter="dragenterCell"
              @drop="drop"
            >
              <div
                v-if="!!props.item.cards[index]"
                :id="props.item.cards[index]?.id"
                :class="{ dragging: props.item.cards[index].id === draggedCardData?.card.id }"
                class="mt-1 mb-1 card-container draggable"
                :draggable="!$store.state.isWorkflowDisabled"
                @dragstart="dragstart"
                @dragend="dragEnd"
              >
                <card-description
                  :item="props.item.cards[index]"
                  :event="findEventFromCardId(props.item.cards[index]?.id)"
                  :disabled="$store.state.isWorkflowDisabled"
                  :compact-replies="true"
                  :showAi="false"
                  :mini-mode="true"
                  :showEntitySelector="true"
                  hideAvatar
                  hide-actions
                  hide-type-selector
                  @ask-for-reply="$emit('ask-for-reply', $event)"
                  @expand-transition-before="startReleaseHeightsAnimation"
                  @expand-transition-after="stopReleaseHeightsAnimation"
                >
                  <card-description-field
                    :item="props.item.cards[index]"
                    :index="1000 * props.item.rowIndex + parseInt(index) + 1"
                    :disabled="$store.state.isWorkflowDisabled"
                    :focus-card="focusCard"
                    @move-focus="moveFocus"
                    :scrollIntoViewArg="true"
                  ></card-description-field>
                </card-description>
              </div>
            </td>
          </template>
          <td
            class="post-column"
            :style="`min-width: ${
              (diagramWidth) -
              noOfColumns * 160
            }px;`"
          ></td>
        </tr>
      </template>
    </v-data-table>
    <card-quick-add
      @focus-card="focusCard = $event.id"
    />
  </div>
</template>

<script>
import CardDescription from '@/components/CardDescription'
import CardDescriptionField from '@/components/CardDescriptionField'
import workflowApi from '@/api/workflowApi'
import { cardHelpers } from '@/mixins/cardHelpers.js'
import cardHelpersUtil from '@/helpers/cardHelpers'
import midString from '@/utils/mid-string'
import { mapState, mapGetters, mapMutations } from 'vuex'
import CardQuickAdd from '@/components/CardQuickAdd'
import QuickRenameInput from '@/components/QuickRenameInput'

export default {
  components: {
    CardDescription,
    CardDescriptionField,
    CardQuickAdd,
    QuickRenameInput
  },

  mixins: [cardHelpers],

  props: {
    items: Array,
    events: Object,
    idIndexMap: Object,
    search: String,
    showDone: Boolean,
    releases: Array,
    workflow: Object
  },

  data () {
    return {
      eventId: null,
      rows: [],
      releaseHeights: {},
      draggedCardData: null,
      draggedCardDataStartingPosition: null,
      synchronizingScroll: false,
      groupBy: [{
        key: 'release',
        order: 'asc'
      }],
      focusCard: null,
      activeExpandTransitions: 0,
      releaseHeightsRafId: null
    }
  },

  computed: {
    ...mapState({
      cardTypes: state => state.cardTypes.cardTypes,
      showReleaseNo: state => state.workflow.showReleaseNo,
      bpmnScrollLeft: state => state.synchronizedScrolling.bpmnScrollLeft,
      sidePanelTab: state => state.sidePanelTab,
      showSidePanel: state => state.showSidePanel
    }),
    ...mapGetters({
      svgGraphData: 'svgGraphData/svgGraphData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGroupsData: 'svgGraphData/svgGroupsData',
      baseData: 'svgGraphData/baseData',
      diagramWidth: 'synchronizedScrolling/diagramWidth'
    }),
    noOfColumns () {
      return Object.keys(this.columns).length
    },

    noOfRows () {
      return this.rowsData.rows.length
    },

    columns () {
      const res = {}
      for (let i = 0; i < this.baseData?.columns?.length; i++) {
        res[i] = this.baseData?.columnsWithCards[i] || {}
      }
      return res
    },

    rowsData () {
      const rows = []
      const displayTypeInBacklog = {}
      const releases = JSON.parse(JSON.stringify(this.releases))
      releases.push({
        value: 'unassigned',
        text: 'unassigned'
      })
      let rowIndex = 0
      let rowsInReleaseCounter
      if (Array.isArray(this.cardTypes)) {
        for (const { id, showInBacklog } of this.cardTypes) {
          displayTypeInBacklog[id] = showInBacklog
        }
      }
      for (const release of releases) {
        rowsInReleaseCounter = 0
        for (const column in this.columns) {
          if (this.columns[column][release.value]?.length > 0) {
            const cards = this.columns[column][release.value]
              .filter(({ done }) => this.showDone === Boolean(done))
              .filter(({ cardType }) => {
                if (!cardType || !Object.prototype.hasOwnProperty.call(displayTypeInBacklog, cardType.id)) {
                  return true
                }
                return displayTypeInBacklog[cardType.id]
              })
            cards.sort((a, b) => {
              if (a.sortkey < b.sortkey) {
                return -1
              } else {
                return 1
              }
            })
            let i = 0
            for (const card of cards) {
              if (!this.showReleaseNo || this.showReleaseNo.length === 0 || this.showReleaseNo.includes(card.releaseNo)) {
                if (!this.search || this.includeInSearch(this.search, card)) {
                  let index = rows.findIndex(row => row.release === release.value && row.rowInRelease === i)
                  if (index >= 0) {
                    if (!rows[index].cards) {
                      rows[index].cards = []
                    }
                    rows[index].cards[column] = card
                  } else {
                    const row = {
                      release: release.value,
                      rowInRelease: i,
                      rowIndex: rowIndex + i
                    }
                    row.cards = []
                    row.cards[column] = card
                    index = rows.push(row) - 1
                  }
                  i++
                }
              }
            }
            if (i > rowsInReleaseCounter) {
              rowsInReleaseCounter = i
            }
          }
        }
        if (rowsInReleaseCounter > 0) {
          rowIndex = rowIndex + rowsInReleaseCounter
        }
      }
      if (!(this.showReleaseNo?.length > 0)) {
        this.fillReleaseGaps(rows)
      }
      return {
        rows
      }
    }
  },

  methods: {
    ...mapMutations({
      setBpmnScrollLeft: 'synchronizedScrolling/setBpmnScrollLeft'
    }),
    getNextUnusedReleaseNo () {
      return 1 + Math.max(...this.rows.map(el => isNaN(el.release) ? 0 : el.release))
    },
    getReleaseName (group) {
      if (group === 'z-release-placeholder') {
        return 'Release ' + this.getNextUnusedReleaseNo()
      } else if (group === 'unassigned') {
        return 'No release assigned'
      } else if (group) {
        // Check if there's a custom name in the workflow.releases array
        const release = this.workflow?.releases?.find(r => r.value === parseInt(group))
        if (release && release.name) {
          return release.name
        }
        return 'Release ' + group
      } else {
        return 'No release assigned'
      }
    },
    async saveReleaseName (releaseValue, event) {
      const newReleaseName = event.target.textContent.trim()

      if (newReleaseName === this.getReleaseName(releaseValue)) {
        return
      }

      if (newReleaseName.length > 30) {
        this.$store.commit('snackbar/showMessage', {
          content: 'Release name must be 30 characters or less',
          color: 'error',
          timeout: 6000
        })
        // Restore the original value
        event.target.textContent = this.getReleaseName(releaseValue)
        return
      }

      try {
        if (newReleaseName.length === 0) {
          const release = this.workflow?.releases?.find((r) => r.value === parseInt(releaseValue))
          if (!release) {
            event.target.textContent = 'Release ' + releaseValue
            return
          }
        }

        await workflowApi.updateReleaseName(
          this.$route.params.id,
          releaseValue,
          newReleaseName
        )
        this.$store.commit('snackbar/showMessage', {
          content: 'Release name updated successfully',
          color: 'success'
        })
      } catch (error) {
        console.error('Error updating release name:', error)
        this.$store.commit('snackbar/showMessage', {
          content: 'Failed to update release name',
          color: 'error'
        })
      }
    },
    firstReleaseIsHigherThanOne (group) {
      if ((group === 'unassigned' && this.releases?.length === 1 && this.releases?.[0]?.value === 'unassigned') || (this.releases[0]?.value === group && group > 1)) {
        return true
      } else {
        return false
      }
    },
    moveFocus (direction, target) {
      // console.log('moveFocus', direction, target)
      switch (direction) {
        case 37: // left arrow
        case 'left':
          this.focusOnPreviousTabindex(target)
          break

        case 38: // up arrow
        case 'up':
          this.focusOnPreviousRow(target)
          break

        case 39: // right arrow
        case 'right':
          this.focusOnNextTabindex(target)
          break

        case 13: // enter
        case 40: // down arrow
        case 'down':
          this.focusOnNextRow(target)
          break
      }
    },
    fillReleaseGaps (rows) {
      for (let i = rows.length - 2; i > -1; i--) {
        if (rows[i].release < (rows[i + 1].release - 1)) {
          rows.splice(i + 1, 0, {
            release: rows[i + 1].release - 1,
            rowInRelease: 0,
            cards: []
          })
          i++
        }
      }
    },
    handleScroll (ev) {
      // when this component is scrolled, also scroll the bpmn component
      const scrollLeft = document.getElementsByClassName('v-table__wrapper')[0].scrollLeft
      this.setBpmnScrollLeft(scrollLeft)
    },
    findHighestNode (eventIds) {
      if (!eventIds || eventIds.length === 0) {
        return null
      }

      const eventIdsWithY = eventIds
        .filter((id) => this.svgGraphData[id])
        .map((id) => ({ y: this.svgGraphData[id].y, id }))

      if (eventIdsWithY.length === 0) {
        return null
      }

      eventIdsWithY.sort((a, b) => a.y - b.y)

      return eventIdsWithY[0].id
    },
    drop (ev) {
      let eventId
      ev.preventDefault()
      const data = ev.dataTransfer.getData('text')
      const events = this.baseData.columns[this.draggedCardData.columnIndex]
      if (this.draggedCardData.columnIndex !== this.draggedCardDataStartingPosition.columnIndex) {
        const eventIds = []
        for (const laneId in events) {
          for (const event of events[laneId]) {
            eventIds.push(event.id)
          }
        }
        eventId = this.findHighestNode(eventIds)
        if (!eventId) {
          this.rows = JSON.parse(JSON.stringify(this.rowsData.rows))
          this.$store.commit('snackbar/showMessage', {
            content: 'Cannot move card to a column with no events. Please add an event to this column first.',
            color: 'warning',
            timeout: 5000
          })

          return
        }
      } else {
        eventId = this.baseData.cardIdToEventIdMap[this.draggedCardDataStartingPosition.card.id]
      }
      const rows = this.rows.filter((row) => row.release === this.draggedCardData.release)
      const sortKeyAbove = rows[this.draggedCardData.rowInRelease - 1]?.cards[this.draggedCardData.columnIndex]?.sortkey || ''
      const sortKeyBelow = rows[this.draggedCardData.rowInRelease + 1]?.cards?.[this.draggedCardData.columnIndex]?.sortkey || ''
      if (this.draggedCardData.release === 'unassigned') {
        this.draggedCardData.release = null
      } else if (this.draggedCardData.release === 'z-release-placeholder') {
        this.draggedCardData.release = this.getNextUnusedReleaseNo()
      }
      const index = this.items.findIndex((req) => req.id === data) // data holds the id
      if (this.items[index]?.eventId === eventId) {
        // only change prio
        this.changeReleaseNo(this.items[index], this.draggedCardData.release, sortKeyAbove, sortKeyBelow)
      } else {
        // change event (and possibly also prio)
        const sortkey = midString(sortKeyAbove, sortKeyBelow)
        workflowApi.moveCard(this.$route.params.id, '', eventId, data, this.draggedCardData.release, sortkey)
      }
    },
    changeReleaseNo (item, releaseNo, sortKeyAbove, sortKeyBelow) {
      const sortkey = midString(sortKeyAbove || '', sortKeyBelow || '')
      if (item.releaseNo === releaseNo && item.sortkey === sortkey) {
        // nothing to do
        return
      }
      workflowApi.updateCardsMutation(this.$route.params.id, [{
        cardId: item.id,
        releaseNo,
        sortkey,
        updateNote: {
          action: 'changed release number of',
          target: cardHelpersUtil.getCardTarget(item)
        }
      }])
    },
    getCardData (id) {
      for (const rowIndex in this.rows) {
        for (const columnIndex in this.rows[rowIndex].cards) {
          if (this.rows[rowIndex].cards[columnIndex]?.id === id) {
            return {
              card: JSON.parse(JSON.stringify(this.rows[rowIndex].cards[columnIndex])),
              rowIndex: parseInt(rowIndex),
              columnIndex: parseInt(columnIndex),
              release: this.rows[rowIndex].release || null,
              rowInRelease: this.rows[rowIndex].rowInRelease
            }
          }
        }
      }
    },
    getDragTarget (ev) {
      if (typeof ev.target.closest === 'function') {
        return ev.target.closest('.card-container')
      } else {
        return ev.target.parentNode.closest('.card-container')
      }
    },
    dragstart (ev) {
      const target = this.getDragTarget(ev)
      this.draggedCardData = this.getCardData(target.id)
      this.draggedCardDataStartingPosition = JSON.parse(JSON.stringify(this.draggedCardData))
      ev.dataTransfer.setData('text', target.id)
      ev.dataTransfer.setData('component', 'user-story-mapping')
      return target.classList?.add('dragging')
    },
    dragEnd (ev) {
      const target = this.getDragTarget(ev)
      target.classList.remove('dragging')
      this.draggedCardData = null
    },
    dragoverGroup (ev, group) {
      const target = ev.target
      if (!target || !this.draggedCardData || group !== this.rows[0].release || this.rows[0].release === 1) {
        return // not the first release so don't do anything
      }
      if (this.rows[0].placeholder === true) {
        return // we already have a placeholder
      }
      this.rows.unshift({
        cards: new Array(this.noOfColumns),
        release: group === 'unassigned' ? 1 : group - 1,
        rowInRelease: 0,
        placeholder: true
      })
      this.$nextTick(() => {
        window.scrollBy({
          top: 60,
          left: 0,
          behavior: 'smooth'
        })
      })
      if (ev) {
        ev.preventDefault()
      }
    },
    dragoverBottomPanel (ev) {
      const lastRow = this.rows.length - 1
      if (this.rows[lastRow].placeholder === true) {
        return // we already have a placeholder
      }
      this.rows.push({
        cards: new Array(this.noOfColumns),
        release: 'z-release-placeholder',
        rowInRelease: 0,
        placeholder: true
      })
      if (ev) {
        ev.preventDefault()
      }
    },
    dragoverCell (ev) {
      ev.preventDefault()
    },
    dragenterCell (ev) {
      const targetCell = this.getTargetCell(ev)
      if (targetCell && !this.sameCell(this.draggedCardData, targetCell)) {
        // restore all cards that might have been moved by dragging around
        if (!targetCell.placeholder &&
          (this.draggedCardData.columnIndex !== targetCell.columnIndex ||
            this.draggedCardData.release !== targetCell.release)) {
          this.rows = JSON.parse(JSON.stringify(this.rowsData.rows))
          this.rows[this.draggedCardDataStartingPosition.rowIndex].cards[this.draggedCardDataStartingPosition.columnIndex] = null
        }

        // assign variables
        const row = this.rows[targetCell.rowIndex]

        // move away colliding cards
        if (targetCell.card && targetCell.card.id !== this.draggedCardData.card.id) {
          if (this.draggedCardData.columnIndex === targetCell.columnIndex &&
            this.draggedCardData.rowIndex === (targetCell.rowIndex - 1) &&
            this.draggedCardData.release === targetCell.release) {
            // drag down from above
            this.moveCardUp(targetCell)
            this.insertDraggedCard(this.draggedCardData, targetCell)
          } else {
            // drag from sides or below
            this.moveCardDown(this.rows, row, targetCell.rowIndex, targetCell.columnIndex, this.draggedCardData.card.id)
            this.insertDraggedCard(this.draggedCardData, targetCell)
          }
        } else {
          // no collision
          this.removeDraggedCard(this.draggedCardData)
          this.insertDraggedCard(this.draggedCardData, targetCell)
        }
      }
      this.computeReleaseHeights()
      ev.preventDefault()
    },
    sameCell (draggedCardData, targetCell) {
      if (draggedCardData.columnIndex === targetCell.columnIndex && draggedCardData.rowIndex === targetCell.rowIndex && draggedCardData.release === targetCell.release) {
        return true
      } else {
        return false
      }
    },
    getTargetCell (ev) {
      let res
      const targetCell = ev.target.closest('.description-column')
      if (targetCell) {
        if (targetCell.dataset.release === 'z-release-placeholder') {
          res = {
            release: 'z-release-placeholder',
            rowInRelease: parseInt(targetCell.dataset.row),
            columnIndex: parseInt(targetCell.dataset.column)
          }
        } else {
          res = {
            release: parseInt(targetCell.dataset.release) || 'unassigned',
            rowInRelease: parseInt(targetCell.dataset.row),
            columnIndex: parseInt(targetCell.dataset.column)
          }
        }
        const targetCellRowIndex = this.rows.findIndex(row => {
          return row.release === res.release && row.rowInRelease === res.rowInRelease
        })
        if (targetCellRowIndex === -1) {
          return null
        }

        res.rowIndex = targetCellRowIndex
        res.card = this.rows[targetCellRowIndex].cards[res.columnIndex] || null
        res.placeholder = this.rows[targetCellRowIndex].placeholder
        return res
      }
    },
    insertDraggedCard (draggedCardData, targetCell) {
      let rowIndex = targetCell.rowIndex
      while (
        rowIndex > 0 &&
        (this.rows[rowIndex - 1].cards[targetCell.columnIndex] === null || this.rows[rowIndex - 1].cards[targetCell.columnIndex] === undefined) &&
        this.rows[rowIndex - 1].release === targetCell.release
      ) {
        rowIndex--
      }
      this.rows[rowIndex].cards[targetCell.columnIndex] = draggedCardData.card
      this.draggedCardData = this.getCardData(this.draggedCardData.card.id)
    },
    removeDraggedCard (draggedCardData) {
      if (this.rows[draggedCardData.rowIndex]?.cards[draggedCardData.columnIndex]?.id === draggedCardData.card.id) {
        this.rows[draggedCardData.rowIndex].cards[draggedCardData.columnIndex] = null
      }
    },
    moveCardUp (targetCell) {
      const targetRow = this.rows[targetCell.rowIndex]
      const targetCard = JSON.parse(JSON.stringify(targetRow.cards[targetCell.columnIndex]))
      this.rows[targetCell.rowIndex - 1].cards[targetCell.columnIndex] = targetCard
      this.rows[targetCell.rowIndex].cards[targetCell.columnIndex] = undefined
    },
    moveCardDown (rows, row, rowIndex, columnIndex, ignoreCardId) {
      let nextRow = this.rows.find(rowObj => {
        return rowObj.release === row.release && rowObj.rowInRelease === row.rowInRelease + 1
      })
      if (!nextRow) {
        const index = this.addEmptyRowToRelease(this.rows, row.release, row.rowInRelease + 1, rowIndex + 1)
        nextRow = this.rows[index]
      }
      if (nextRow.cards[columnIndex] && nextRow.cards[columnIndex].id !== ignoreCardId) {
        this.moveCardDown(rows, nextRow, rowIndex + 1, columnIndex)
      }
      nextRow.cards[columnIndex] = row.cards[columnIndex]
      row.cards[columnIndex] = undefined
    },
    addEmptyRowToRelease (rows, release, rowInRelease, rowIndex) {
      this.rows.splice(rowIndex, 0, {
        release,
        rowInRelease,
        cards: []
      })
      return rowIndex
    },
    includeInSearch (search, card) {
      if (typeof search !== 'string' || !search.trim()) {
        return true
      }

      const {
        id,
        description,
        releaseNo,
        cardType,
        status,
        creator,
        eventId
      } = card
      let computedStatus

      if (status) {
        const statusValues = ['mo', 's', 'co', 'w']
        const prioIndex = status.prio - 1

        computedStatus = statusValues[prioIndex]
      }

      const searchFields = [
        id,
        description,
        releaseNo,
        cardType && cardType.title,
        computedStatus,
        creator && `${creator.firstname} ${creator.lastname}`,
        eventId
      ].filter(Boolean).join(' ').toLowerCase()
      const searchText = search.toString().toLowerCase()

      return searchFields.includes(searchText)
    },
    focusOnThisCell (event) {
      const pre = event.target.querySelector('pre')
      if (pre) {
        pre.focus()
      }
    },
    focusOnNextRow (target) {
      let nextRowTabindex = parseInt(target.tabIndex) + 1000
      if (nextRowTabindex > 0) {
        while (nextRowTabindex <= this.noOfRows * 1000) {
          const nextElement = document.querySelector(`[tabindex='${nextRowTabindex}']`)
          if (nextElement) {
            nextElement.focus()
            return true
          }
          nextRowTabindex += 1000
        }
      }
      target.blur()
      target.focus()
    },
    focusOnPreviousRow (target) {
      let prevRowTabindex = parseInt(target.tabIndex) - 1000
      if (prevRowTabindex > 0) {
        while (prevRowTabindex >= 0) {
          const previousElement = document.querySelector(`[tabindex='${prevRowTabindex}']`)
          if (previousElement) {
            previousElement.focus()
            break
          }
          prevRowTabindex -= 1000
        }
      }
    },
    focusOnPreviousTabindex (target) {
      let prevTabindex = parseInt(target.tabIndex) - 1
      const startPosition = prevTabindex
      if (prevTabindex > 0) {
        while (prevTabindex > (startPosition - this.noOfColumns)) {
          const nextElement = document.querySelector(`[tabindex='${prevTabindex}']`)
          if (nextElement && nextElement.classList && nextElement.classList.contains('ql-editor')) {
            nextElement.focus()
            break
          }
          prevTabindex--
        }
      }
    },
    focusOnNextTabindex (target) {
      let nextTabindex = parseInt(target.tabIndex) + 1
      const startPosition = nextTabindex
      if (nextTabindex > 0) {
        while (nextTabindex <= (startPosition + this.noOfColumns)) {
          const nextElement = document.querySelector(`[tabindex='${nextTabindex}']`)
          if (nextElement) {
            nextElement.focus()
            break
          }
          nextTabindex++
        }
      }
    },
    startReleaseHeightsAnimation () {
      this.activeExpandTransitions++
      if (this.releaseHeightsRafId !== null) {
        return
      }
      const tick = () => {
        this.measureReleaseHeights()
        this.releaseHeightsRafId = requestAnimationFrame(tick)
      }
      this.releaseHeightsRafId = requestAnimationFrame(tick)
    },
    stopReleaseHeightsAnimation () {
      this.activeExpandTransitions = Math.max(0, this.activeExpandTransitions - 1)
      if (this.activeExpandTransitions === 0 && this.releaseHeightsRafId !== null) {
        cancelAnimationFrame(this.releaseHeightsRafId)
        this.releaseHeightsRafId = null
      }
      this.computeReleaseHeights()
    },
    measureReleaseHeights () {
      // HACK: calculating the height of each release
      // There is probably a better way to do this but for saving time I just rotate a div and use each
      // Y coordinate to compute the height by comparing previous Y coordinate with the current one.
      let firstY = 0
      let lastY = 0
      let lastRelease = null
      const scrollTop = document.documentElement.scrollTop
      const maxReleaseNumber = this.releases.length ? Math.max(...this.releases.map(r => r.value)) : 0
      const releases = maxReleaseNumber ? Array(maxReleaseNumber).fill(0).map((_, i) => i + 1) : []

      releases.forEach(release => {
        this.releaseHeights[release] = 120

        if (!this.$refs[`release-${release}`]) return
        const thisY = this.$refs[`release-${release}`]?.getBoundingClientRect()?.y + scrollTop || lastY

        if (lastRelease) {
          this.releaseHeights[lastRelease] = thisY - lastY
        }
        lastY = thisY
        lastRelease = release
        if (firstY === 0) {
          firstY = lastY
        }
      })

      const tableHeight = (this.$refs.table?.$el?.getBoundingClientRect()?.height || 40) - 40
      const unassignedY = this.$refs['release-unassigned'] ? this.$refs['release-unassigned']?.getBoundingClientRect()?.y + scrollTop : (lastY + tableHeight)
      if (lastRelease) {
        this.releaseHeights[lastRelease] = firstY + tableHeight - lastY
        lastY = unassignedY
      }
      this.releaseHeights.unassigned = firstY + tableHeight - lastY
      if (this.releaseHeights.unassigned && this.releaseHeights.unassigned > 0) {
        this.releaseHeights[lastRelease] -= this.releaseHeights.unassigned
      }
    },
    computeReleaseHeights () {
      this.$nextTick(() => {
        this.measureReleaseHeights()
      })
    },
    findEventFromCardId (cardId) {
      const eventId = this.baseData.cardIdToEventIdMap[cardId]
      if (eventId) {
        return this.workflow.domainEvents?.find((event) => event.id === eventId)
      }
      return null
    }
  },
  mounted () {
    this.$nextTick(() => {
      const element = document.getElementsByClassName('v-table__wrapper')[0]
      element.addEventListener('scroll', this.handleScroll)
      this.rows = JSON.parse(JSON.stringify(this.rowsData.rows))
      // const element2 = document.getElementsByClassName('dragover-to-add-release')[0]
      // element2.addEventListener('dragover', this.dragoverBottomPanel)

      this.$nextTick(() => {
        this.computeReleaseHeights()
      })
    })
  },
  beforeUnmount () {
    if (this.releaseHeightsRafId !== null) {
      cancelAnimationFrame(this.releaseHeightsRafId)
      this.releaseHeightsRafId = null
    }
    let res = document.getElementsByClassName('v-table__wrapper')[0]?.removeEventListener('scroll', this.handleScroll)
    res = document.getElementsByClassName('dragover-to-add-release')[0]?.removeEventListener('dragover', this.dragoverBottomPanel)
    return res // need to assign a variable and use it to be able to use the optional chaining operator (?)
  },
  watch: {
    bpmnScrollLeft (newVal) {
      // receive scroll event from bpmn component
      document.getElementsByClassName('v-table__wrapper')[0].scrollLeft = newVal
    },
    rowsData: {
      handler (newRowsData, oldRowsData) {
        this.rows = JSON.parse(JSON.stringify(this.rowsData.rows))
        this.computeReleaseHeights()
      },
      deep: true
    },
    showDone () {
      this.$nextTick(() => {
        this.computeReleaseHeights()
      })
    },
    search () {
      this.$nextTick(() => {
        this.computeReleaseHeights()
      })
    },
    releases: {
      handler () {
        this.computeReleaseHeights()
      },
      deep: true
    }
  }

}
</script>

<style scoped>
.v-btn:not(.v-btn--depressed):not(.v-btn--flat) {
  box-shadow: none;
}

.dragover-to-add-release-top {
  border: 2px dashed gray;
}

[contenteditable="true"] br {
  display: none;
}

.btn {
  /* sets the background for the base class */
  background: rgb(var(--red), var(--green), var(--blue));

  /* calculates perceived lightness using the sRGB Luma method
    Luma = (red * 0.2126 + green * 0.7152 + blue * 0.0722) / 255 */
  --r: calc(var(--red) * 0.2126);
  --g: calc(var(--green) * 0.7152);
  --b: calc(var(--blue) * 0.0722);
  --sum: calc(var(--r) + var(--g) + var(--b));
  --perceived-lightness: calc(var(--sum) / 255);

  /* shows either white or black color depending on perceived darkness */
  color: hsl(
    0,
    0%,
    calc((var(--perceived-lightness) - var(--threshold)) * -10000000%)
  );
}

:deep(.v-table__wrapper) {
  padding-bottom: 2px;
}

.usm-release {
  height: 34px;
  position: absolute;
  padding: 5px 8px;
  background: var(--light-4);
  text-align: center;
  color: var(--text-dark);
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  z-index: 2;
}

.release-name-display {
  width: 100%;
  text-align: center;
  font-weight: 500;
}

.usm-release.first {
  border-top-right-radius: 4px;
}

.usm-release.last {
  border-top-left-radius: 4px;
}

td.description-column {
  border-right: thin solid rgba(0, 0, 0, 0.12);
  padding-left: 8px !important;
  padding-right: 8px !important;
  min-width: 160px;
  max-width: 160px;
  width: 160px;
  vertical-align: top;
  z-index: 0;
}

.v-tooltip__content {
  background: var(--light-1);
  color: var(--color-primary);
  border-radius: 10px;
}

.v-tooltip__content code {
  background: var(--color-primary);
  color: white;
}

.sortable-chosen {
  background: #eee;
}

[data-theme="dark"] .sortable-chosen {
  background: var(--light-3);
}

.white-background-on-hover:hover {
  background: var(--light-1) !important;
}

.v-data-table > .v-table__wrapper > table {
  width: 120px;
}

:deep(.v-table__wrapper) {
  width: fit-content;
  min-width: 100%;
  max-width: calc(100% + 40px);
}

.draggable {
  cursor: move;
}

.draggable.dragging {
  opacity: 0.01;
}

html {
  scroll-behavior: smooth;
}

td.pre-column {
  border-bottom: 0px !important;
  padding: 0px !important;
  width: 24px;
  min-width: 24px;
  max-width: 24px;
  min-height: 120px;
  height: 120px !important;
}

td:has(.dragging) {
  background-color: #E5E3E1;
}

[data-theme="dark"] td:has(.dragging) {
  background-color: var(--light-3);
}

.row-start-margin {
  width: 26px !important;
  min-width: 26px !important;
  padding: 0 !important;
}

</style>
